import { usePageStore } from '@/Components/templates/PageStore';
import type { SetStateFn } from '@/Types/react';
import { useZnglyContext } from '@/Utility/context/ZnglyAppContext';
import { debounce } from '@/Utility/utils';
import { updateZnglyInfoSettingsMutation } from '@/graphql/mutations/ZnglyInfo.mutation';
import type {
  ZnglyInfo_UpdateSettings_Mutation$data,
  ZnglyInfo_UpdateSettings_Mutation$variables,
} from '@/graphql/mutations/__generated__/ZnglyInfo_UpdateSettings_Mutation.graphql';
import { MimeTypeEnum } from '@/graphql/types';
import FindReplaceIcon from '@mui/icons-material/FindReplace';
import type { SelectChangeEvent } from '@mui/material/Select';
import type React from 'react';
import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { useSettingsStore } from './useSettingsStore';

export const updateSettingsExtraInputs = {
  isIntegrations: false,
  isAnalytics: false,
  isBackend: false,
  isPrivacy: false,
  isSiteSettings: false,
  isGlobalStyles: false,
  isQrCode: false,
  isHeader: false,
  isFooter: false,
  isPage: false,
  isHomepage: false,
  isPersonalisedVideo: false,
};

export type AssetNode = {
  id?: string;
  mediaItemUrl?: string;
  title?: string;
};

export type CommitProps = {
  onBlur: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLDivElement>) => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void;
};

export type UpdateSettingsExtraInputs = typeof updateSettingsExtraInputs;
type Input = Partial<ZnglyInfo_UpdateSettings_Mutation$variables['input']>;

type StateType = Record<string, string | boolean | AssetNode>;

export const useSettingsUtil = <InitState extends StateType, State extends StateType>(
  initState: InitState,
  state: State,
  setState: SetStateFn<State>,
  updateFnKey: keyof typeof updateSettingsExtraInputs,
  input: Input
) => {
  const queryRef = useSettingsStore((state) => state.queryRef);
  const pageLoading = usePageStore((state) => state.pageLoading);
  const { appData } = useZnglyContext();

  const handleEvent = (
    key: keyof State,
    event:
      | React.KeyboardEvent<HTMLDivElement | HTMLInputElement | HTMLTextAreaElement>
      | React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
      | React.SyntheticEvent
  ) => {
    const value: string = (event as React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>).target?.value;

    // if (value === initState[key]) return;
    if (Object.hasOwn(initState, key) && (initState as unknown as State)[key] === value) return;
    // check if there is an object with an id or title
    if (typeof (initState as unknown as State)[key] === 'object' && (initState[key] as AssetNode)?.id === value) return;
    if (typeof (initState as unknown as State)[key] === 'object' && (initState[key] as AssetNode)?.title === value)
      return;

    // check if its blur event or keypress event
    if (event.type === 'blur') {
      commitMutation(input);
    } else if (event.type === 'keydown') {
      if ((event as React.KeyboardEvent<HTMLDivElement>).key === 'Enter') {
        // blur the input, which will trigger the commitMutation
        (event as React.FocusEvent<HTMLDivElement>).target?.blur();
      }
    }
  };

  const handleChange = (
    key: keyof State,
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string> | null,
    value?: string | null
  ) => {
    if (event?.target?.value === state?.[key]) return;
    if (!!value) setState({ ...state, [key]: value });
    else if (value === null) setState({ ...state, fontStyle: initState.fontStyle });
    else if (event?.target?.value) setState({ ...state, [key]: event.target?.value });
    else if (!event?.target?.value) {
      if (
        ((initState as unknown as State)?.[key] && typeof (initState as unknown as State)[key] === 'string') ||
        typeof state[key] === 'string'
      )
        setState({ ...state, [key]: '' });
      else if (Array.isArray(initState[key]) || Array.isArray(state[key])) setState({ ...state, [key]: [] });
      else setState({ ...state, [key]: null });
    }
  };

  const textEventProps = (key: keyof State) => ({
    onBlur: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      handleEvent(key, event);
    },
    onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => {
      handleEvent(key, event);
    },
    onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      handleChange(key, event);
    },
    value: state[key],
  });

  const commitProps = (key: keyof State) => ({
    onBlur: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLDivElement>) => {
      handleEvent(key, event);
    },
    onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => {
      handleEvent(key, event);
    },
  });

  const autocompleteEventProps = (key: keyof State) => ({
    onBlur: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      handleEvent(key, event);
    },
    onChange: (value: string | null) => {
      handleChange(key, null, value);
    },
  });

  const selectEventProps = (key: keyof State) => ({
    onChange: (event: SelectChangeEvent<string>, child: React.ReactNode) => {
      handleChange(key, event);
    },
    value: state[key],
  });

  // very important - do not pass any object to the debounce function.
  // this will cause the object to only be passed once, your action will
  // not contain the new value of the object.
  const onChangeColorInputDebounced = useCallback(
    debounce((key: string, value: string) => {
      handleChange(key, { target: { value } } as unknown as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>);
    }, 100),
    []
  );

  const inputColorEventProps = (key: keyof typeof state) => ({
    onBlur: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      handleEvent(key, event);
    },
    onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChangeColorInputDebounced(key as string, event.target.value);
    },
    onKeyDown: (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      handleEvent(key, event);
    },
    value: state[key],
    disableUnderline: true,
    type: 'color',
  });

  const colorInputProps = (key: keyof State) => {
    return {
      initValue: initState[key] as string,
      onPopoverClose: (color: string) => {
        if ((initState as unknown as State)[key] === color) return;
        commitMutation(input);
      },
      onChange: (color: string, optimisticCommit = false) => {
        setState({
          ...state,
          [key]: color,
        });
        if (optimisticCommit) {
          commitMutation({ ...input, [key]: color });
        }
      },
      presetColors: appData.colorPresets,
    };
  };

  const fileEventProps = (key: keyof typeof state, mime: 'image' | 'video' = 'image') => {
    return {
      onClear: () => {
        if (!queryRef?.znglyInfo?.id) return;

        usePageStore.setState({ pageLoading: true });
        setState({ ...state, [key]: null });
        updateZnglyInfoSettingsMutation(
          {
            input: {
              id: queryRef?.znglyInfo?.id,
              [key]: null,
            },
            ...updateSettingsExtraInputs,
            [updateFnKey]: true,
          },
          {
            onCompleted: (data, errors) => {
              if (!!errors?.length) {
                toast.error(`Could not update: ${errors.map((e) => e.message).join(', ')}`);
              }

              usePageStore.setState({ pageLoading: false });
            },
          }
        );
      },
      onFileSelect: (file: AssetNode | null) => {
        if (file === null || !file) return;
        if (file?.id === (state[key] as AssetNode)?.id) return;
        if (!queryRef?.znglyInfo?.id) return;

        setState({ ...state, [key]: file });

        if (pageLoading) {
          toast.error('Please wait for the current update to finish');
          return;
        }
        usePageStore.setState({ pageLoading: true });
        updateZnglyInfoSettingsMutation(
          {
            input: {
              id: queryRef?.znglyInfo?.id,
              [key]: file?.id,
            },
            ...updateSettingsExtraInputs,
            [updateFnKey]: true,
          },
          {
            onCompleted: (data, errors) => {
              if (!!errors?.length) {
                toast.error(`Could not update: ${errors.map((e) => e.message).join(', ')}`);
              }
              usePageStore.setState({ pageLoading: false });
            },
          }
        );
      },
      mimeTypes:
        (mime === 'image' && [MimeTypeEnum.ImageJpeg, MimeTypeEnum.ImageGif, MimeTypeEnum.ImagePng]) ||
        (mime === 'video' && [MimeTypeEnum.VideoMp4]),
      buttonTitle: 'Replace',
      buttonIcon: <FindReplaceIcon />,
      buttonStyle: { width: '100%' } as React.CSSProperties,
    };
  };

  const commitMutation = (input: Input, callback?: (data: ZnglyInfo_UpdateSettings_Mutation$data) => void) => {
    if (!queryRef?.znglyInfo?.id) return;
    // only allow inputs with attributes that are string, boolean or number, not arrays
    const updatedInput = Object.keys(input).reduce((acc, key) => {
      const k = key as keyof Input;
      if (
        typeof input[k] === 'string' ||
        typeof input[k] === 'boolean' ||
        typeof input[k] === 'number' ||
        input[k] === null
      ) {
        // @ts-ignore
        acc[key] = input[k];
      } else if (typeof input[k] === 'object') {
        // test for media file input
        if ((input[k] as AssetNode)?.mediaItemUrl) return acc;

        // test object has only string, number or boolean values
        // this is used for grouped inputs like the social links inputs
        const objKeys = Object.keys(input[k]);
        if (objKeys.length === 0) return acc;
        if (
          objKeys.every((everyKey) => {
            const ek = everyKey as keyof (typeof input)[typeof k];
            return (
              typeof input?.[k]?.[ek] === 'string' ||
              typeof input?.[k]?.[ek] === 'boolean' ||
              typeof input?.[k]?.[ek] === 'number' ||
              input?.[k]?.[ek] === null ||
              input?.[k]?.[ek] === undefined
            );
          })
        ) {
          // @ts-ignore
          acc[k] = input[k];
        }
      }
      return acc;
    }, {}) as Input;

    if (pageLoading) {
      toast.error('Please wait for the current update to finish');
      return;
    }
    usePageStore.setState({ pageLoading: true });
    updateZnglyInfoSettingsMutation(
      {
        input: {
          id: queryRef?.znglyInfo?.id,
          ...updatedInput,
        },
        ...updateSettingsExtraInputs,
        [updateFnKey]: true,
      },
      {
        onCompleted: (data: ZnglyInfo_UpdateSettings_Mutation$data, errors) => {
          usePageStore.setState({ pageLoading: false });
          if (!!errors?.length) {
            toast.error(`Could not update: ${errors.map((e) => e.message).join(', ')}`);
          }
          callback?.(data);
        },
      }
    );
  };

  // commit mutation function withouth the input pre-processing
  const externalCommitMutation = (input: Input, callback?: (data: ZnglyInfo_UpdateSettings_Mutation$data) => void) => {
    if (!queryRef?.znglyInfo?.id) return;
    if (pageLoading) {
      toast.error('Please wait for the current update to finish');
      return;
    }

    usePageStore.setState({ pageLoading: true });

    updateZnglyInfoSettingsMutation(
      {
        input: {
          id: queryRef.znglyInfo.id,
          ...input,
        },
        ...updateSettingsExtraInputs,
        [updateFnKey]: true,
      },
      {
        onCompleted: (data: ZnglyInfo_UpdateSettings_Mutation$data, errors) => {
          usePageStore.setState({ pageLoading: false });
          if (!!errors?.length) {
            toast.error(`Could not update: ${errors.map((e) => e.message).join(', ')}`);
          }
          callback?.(data);
        },
      }
    );
  };

  return {
    textEventProps,
    autocompleteEventProps,
    fileEventProps,
    inputColorEventProps,
    selectEventProps,
    commitProps,
    commitMutation: externalCommitMutation,
    colorInputProps,
  };
};

export const useSettingsCommitMutation = (updateFnKey: keyof typeof updateSettingsExtraInputs) => {
  const queryRef = useSettingsStore((state) => state.queryRef);
  const pageLoading = usePageStore((state) => state.pageLoading);

  const commitMutation = (
    input: Omit<Input, 'id'>,
    callback?: (data: ZnglyInfo_UpdateSettings_Mutation$data) => void
  ) => {
    if (!queryRef?.znglyInfo?.id) return;
    if (pageLoading) {
      toast.error('Please wait for the current update to finish');
      return;
    }

    usePageStore.setState({ pageLoading: true });
    updateZnglyInfoSettingsMutation(
      {
        input: {
          id: queryRef.znglyInfo.id,
          ...input,
        },
        ...updateSettingsExtraInputs,
        [updateFnKey]: true,
      },
      {
        onCompleted: (data: ZnglyInfo_UpdateSettings_Mutation$data, errors) => {
          usePageStore.setState({ pageLoading: false });
          if (!!errors?.length) {
            toast.error(`Could not update: ${errors.map((e) => e.message).join(', ')}`);
          }
          callback?.(data);
        },
      }
    );
  };

  return {
    commitMutation,
  };
};
